import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import api from '../config/api';
import NumericTextField from './NumericTextField';
import { DOCUMENT, documentOutlineButtonSx } from '../constants/documentTheme';
import { partnerOxbloodBtnSx } from './PartnerListChrome';

const PARTNER_AUTH = { usePartnerAuth: true };

const BookForClientDialog = ({
  open,
  onClose,
  packageId,
  packageTitle,
  currency = 'USD',
  pricePerPerson = 0,
  defaultEmail = '',
  defaultTravelers = 1,
  pricingId = null,
  onSent,
}) => {
  const { t } = useTranslation();
  const [email, setEmail] = useState(defaultEmail);
  const [travelers, setTravelers] = useState(String(defaultTravelers || 1));
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [sent, setSent] = useState(null);

  useEffect(() => {
    if (!open) return;
    setEmail(defaultEmail || '');
    setTravelers(String(defaultTravelers || 1));
    setBusy(false);
    setError('');
    setSent(null);
  }, [open, defaultEmail, defaultTravelers]);

  const count = Math.max(1, parseInt(travelers, 10) || 1);
  const total = useMemo(
    () => Number(pricePerPerson || 0) * count,
    [pricePerPerson, count]
  );

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!packageId || !email.trim()) return;
    try {
      setBusy(true);
      setError('');
      const response = await api.post(
        `/package-booking/partner/packages/${packageId}/book-for-client`,
        {
          email: email.trim(),
          travelers: count,
          pricingId,
        },
        PARTNER_AUTH
      );
      const payload = {
        email: response.data.email,
        bookingReference: response.data.bookingReference,
        totalPrice: response.data.totalPrice,
        currency: response.data.currency || currency,
      };
      setSent(payload);
      if (onSent) onSent(payload);
    } catch (err) {
      setError(err.response?.data?.error || t('bookForClient.failed'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={busy ? undefined : onClose}
      fullWidth
      maxWidth="sm"
      data-testid="book-for-client-dialog"
    >
      <DialogTitle sx={{ fontFamily: DOCUMENT.display, fontWeight: 400, pr: 3 }}>
        {t('bookForClient.title')}
      </DialogTitle>
      <DialogContent>
        <Typography sx={{ fontSize: 14, color: DOCUMENT.ink2, mb: 2.5 }}>
          {t('bookForClient.subtitle', { title: packageTitle || t('bookForClient.thisPackage') })}
        </Typography>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        {sent ? (
          <Alert severity="success" data-testid="book-for-client-sent">
            {t('bookForClient.sent', {
              email: sent.email,
              ref: sent.bookingReference,
            })}
          </Alert>
        ) : (
          <Box component="form" id="book-for-client-form" onSubmit={handleSubmit}>
            <TextField
              autoFocus
              fullWidth
              required
              type="email"
              label={t('bookForClient.email')}
              value={email}
              onChange={(event) => setEmail(event.target.value)}
              sx={{ mb: 2.25 }}
              inputProps={{ 'data-testid': 'book-for-client-email' }}
            />
            <NumericTextField
              fullWidth
              required
              label={t('bookForClient.travelers')}
              value={travelers}
              onValueChange={(value) => setTravelers(value)}
              min={1}
              allowEmpty={false}
              inputProps={{ min: 1, 'data-testid': 'book-for-client-travelers' }}
            />
            {total > 0 && (
              <Typography sx={{ mt: 2, fontSize: 14, color: DOCUMENT.ink2 }}>
                {t('bookForClient.total', {
                  amount: `${Number(total).toFixed(0)} ${currency}`,
                  count,
                })}
              </Typography>
            )}
          </Box>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2.5 }}>
        <Button onClick={onClose} sx={documentOutlineButtonSx}>
          {sent ? t('common.close') : t('common.cancel')}
        </Button>
        {!sent && (
          <Button
            type="submit"
            form="book-for-client-form"
            disabled={busy || !email.trim()}
            sx={partnerOxbloodBtnSx}
          >
            {busy ? t('bookForClient.sending') : t('bookForClient.send')}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default BookForClientDialog;
